/**
 * Input contracts for the authentication endpoints (PRD §8), shared by the forms and the API
 * so the client cannot send something the server has to invent an error for.
 */

import { z } from "zod";

/** Long enough to matter, short enough that invited admins finish the setup screen. */
export const MIN_PASSWORD_LENGTH = 10;

export const emailSchema = z.string().trim().toLowerCase().email().max(255);

export const localeSchema = z.enum(["de", "en"]).default("de");

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});

export const magicLinkRequestSchema = z.object({
  email: emailSchema,
  locale: localeSchema,
});

export const setPasswordSchema = z.object({
  password: z.string().min(MIN_PASSWORD_LENGTH).max(200),
  currentPassword: z.string().max(200).optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;

export type SessionRole = "grantonomy_admin" | "company_admin" | "employee";

/** What `GET /api/auth/session` returns; the shell renders its nav from this. */
export interface SessionResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: SessionRole;
    companyId: string | null;
    passwordSetupRequired: boolean;
  } | null;
}

/**
 * Where a signed-in person belongs when they land on `/`, `/login` or come back from a one-time
 * link. Shared so the server's redeem redirect and the client's guards cannot drift apart.
 */
export function homePath(input: { role: SessionRole; passwordSetupRequired: boolean }): string {
  if (input.passwordSetupRequired) return "/set-password";
  return input.role === "employee" ? "/my/tracking" : "/";
}
